import type { TeleconsultManagementVisit } from '../../types/api';
import { secondsBetween } from './formatManagementTime';

/** Journey hop targets (seconds) — keep in sync with dashboard KPI targets. */
export const JOURNEY_KPI = {
  id: 10 * 60,
  start: 15 * 60,
  consult: 15 * 60,
  checkout: 5 * 60,
} as const;

export type JourneyStepKey =
  | 'registeredAt'
  | 'idVerifiedAt'
  | 'stripePaidAt'
  | 'patientReadyAt'
  | 'doctorPickedAt'
  | 'consultCompletedAt'
  | 'juliusCheckoutAt';

export type JourneyStepDef = {
  key: JourneyStepKey;
  label: string;
  hopTarget: number | null;
};

const PAYMENT_STEP: JourneyStepDef = { key: 'stripePaidAt', label: 'Payment', hopTarget: null };

/** Fixed columns so Start / End / Checkout line up whether or not the row has Payment. */
export const LOG_STEPS: JourneyStepDef[] = [
  { key: 'registeredAt', label: 'Register', hopTarget: null },
  { key: 'idVerifiedAt', label: 'ID verify', hopTarget: JOURNEY_KPI.id },
  PAYMENT_STEP,
  { key: 'patientReadyAt', label: 'Pt ready', hopTarget: null },
  { key: 'doctorPickedAt', label: 'Start', hopTarget: JOURNEY_KPI.start },
  { key: 'consultCompletedAt', label: 'End', hopTarget: JOURNEY_KPI.consult },
  { key: 'juliusCheckoutAt', label: 'Checkout', hopTarget: JOURNEY_KPI.checkout },
];

/** Max upload->approve / within-visit ID hop kept (excludes multi-day pre-visit outliers). */
const ID_KPI_MAX_SECONDS = 12 * 60 * 60;

/** Stripe / payment-related visits get a Payment hop before Pt ready. Corporate rows stay unchanged. */
export function showsPaymentHop(visit: TeleconsultManagementVisit): boolean {
  if (visit.stripePaidAt) return true;
  if ((visit.type || '').trim().toLowerCase() === 'stripe') return true;
  const reason = `${visit.cancellationReason || ''} ${visit.refundReason || ''}`;
  return /payment/i.test(reason);
}

export function journeyStepsFor(_visit: TeleconsultManagementVisit): JourneyStepDef[] {
  return LOG_STEPS;
}

export function isCancelledOrRefunded(visit: TeleconsultManagementVisit): boolean {
  return visit.status === 'cancelled' || visit.status === 'refunded';
}

export function journeyStamps(visit: TeleconsultManagementVisit): Array<string | null | undefined> {
  return journeyStepsFor(visit).map((s) => visit[s.key] as string | null | undefined);
}

export type JourneyHopView = {
  step: JourneyStepDef;
  at: string | null | undefined;
  done: boolean;
  current: boolean;
  hopIn: number | null;
  hopOut: number | null;
  overIn: boolean;
  overOut: boolean;
  linked: boolean;
  /** Column kept for alignment when this visit has no payment hop. */
  placeholder: boolean;
  /** Dim hops on cancelled/refunded visits so unpaid cancels do not look successful. */
  dimmed: boolean;
  hint?: string;
  className: string;
};

function hopOverTarget(seconds: number | null, target: number | null): boolean {
  if (seconds == null) return false;
  if (target != null) return seconds > target;
  return seconds > 10 * 60;
}

/**
 * Within-visit ID hop for Patient Log journey.
 * Prefer upload->approve when the Patient ID was uploaded during this visit.
 * If upload (IdPendingAt) is before Register, clamp start to Register so pre-visit /
 * prior-session attachments never produce multi-week +Nm deltas on a same-day visit.
 * Falls back to Register->Verified when upload stamp is missing.
 * Still-absurd gaps (missing/corrupt stamps) return null so the UI omits the hop label.
 * KPI Pending->Verified stays on {@link idVerificationSampleSeconds} (true upload, 12h cap).
 */
export function idVerifyJourneyHopSeconds(visit: TeleconsultManagementVisit): number | null {
  const end = visit.idVerifiedAt;
  if (!end) return null;

  const upload = visit.idPendingAt;
  const reg = visit.registeredAt;

  let start: string | null | undefined = upload ?? reg ?? null;
  if (upload && reg) {
    // upload before/at Register -> clamp to Register; upload after Register -> keep upload.
    const uploadBeforeOrAtReg = secondsBetween(upload, reg);
    if (uploadBeforeOrAtReg != null) {
      start = reg;
    }
  }

  if (!start) return null;

  const sec = secondsBetween(start, end);
  if (sec == null || sec > ID_KPI_MAX_SECONDS) return null;
  return sec;
}

export function buildJourneyHops(visit: TeleconsultManagementVisit): JourneyHopView[] {
  const steps = journeyStepsFor(visit);
  const stamps = steps.map((s) => visit[s.key] as string | null | undefined);
  const dimmedVisit = isCancelledOrRefunded(visit);
  const showPayment = showsPaymentHop(visit);
  const isPlaceholder = (index: number) => steps[index]?.key === 'stripePaidAt' && !showPayment;
  const nextRealIndex = (from: number) => {
    for (let j = from + 1; j < steps.length; j++) {
      if (!isPlaceholder(j)) return j;
    }
    return -1;
  };
  const prevRealIndex = (from: number) => {
    for (let j = from - 1; j >= 0; j--) {
      if (!isPlaceholder(j)) return j;
    }
    return -1;
  };

  return steps.map((step, i) => {
    const placeholder = isPlaceholder(i);
    const at = placeholder ? undefined : stamps[i];
    const prevFilled = stamps.slice(0, i).reverse().find(Boolean);
    const nextReal = nextRealIndex(i);
    const prevReal = prevRealIndex(i);
    const nextAt = nextReal >= 0 ? stamps[nextReal] : undefined;
    const lineFrom = placeholder ? (prevReal >= 0 ? stamps[prevReal] : undefined) : at;
    const lineTo = nextAt;
    const nextStep = nextReal >= 0 ? steps[nextReal] : undefined;
    const hopIn =
      placeholder || i === 0
        ? null
        : step.key === 'idVerifiedAt'
          ? idVerifyJourneyHopSeconds(visit)
          : secondsBetween(prevFilled, at);
    const hopOut = secondsBetween(lineFrom, lineTo);
    const overIn = hopOverTarget(hopIn, step.hopTarget);
    const overOut = hopOverTarget(hopOut, nextStep?.hopTarget ?? null);
    const done = Boolean(at);
    const current = done && !stamps.slice(i + 1).some(Boolean);
    const linked = Boolean(lineFrom && lineTo);
    const dimmed = dimmedVisit;
    const hint =
      !placeholder && step.key === 'stripePaidAt' && !done
        ? dimmed
          ? 'Payment not completed'
          : 'Awaiting payment'
        : undefined;

    const classes = [
      'mdash-log-flow__step',
      placeholder ? 'is-placeholder' : done ? (overIn ? 'is-over' : 'is-done') : 'is-todo',
      !placeholder && current ? 'is-current' : '',
      linked ? (overOut ? 'is-linked-over' : 'is-linked') : '',
      dimmed ? 'is-dimmed' : '',
      !placeholder && step.key === 'stripePaidAt' && !done ? 'is-unpaid' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return {
      step,
      at,
      done,
      current,
      hopIn,
      hopOut,
      overIn,
      overOut,
      linked,
      placeholder,
      dimmed,
      hint,
      className: classes,
    };
  });
}

/** ID KPI / staff ID time: Patient ID upload (IdPendingAt) -> staff/AI DateVetted. No RegStart fallback. */
export function idVerificationSampleSeconds(visit: TeleconsultManagementVisit): number | null {
  const end = visit.idVerifiedAt;
  const start = visit.idPendingAt;
  if (!end || !start) return null;
  const sec = secondsBetween(start, end);
  if (sec == null || sec > ID_KPI_MAX_SECONDS) return null;
  return sec;
}

/**
 * Per-staff handling duration, analogous to journey middle-waits:
 * - Dispense: consult end -> Julius checkout
 * - ID verification: upload -> DateVetted (same as ID KPI)
 */
export function staffHandlingSampleSeconds(visit: TeleconsultManagementVisit): number | null {
  const step = (visit.handlingStaffStep || '').trim();
  if (step === 'Dispense') {
    return secondsBetween(visit.consultCompletedAt, visit.juliusCheckoutAt);
  }
  if (step === 'ID verification') {
    return idVerificationSampleSeconds(visit);
  }
  return null;
}